import { useMemo } from 'react';

import { B2bCompanyPlan } from '@Common/types/bo/b2b/plans';
import { GetInstrumentsResponseItem } from '@Common/types/bo/instruments';
import { B2bInstrumentType } from '@Common/types/instruments/instrument';

export const useB2bPlanEntryFees = ({
  instrumentsData,
  isFetched,
  planEntryFeesBase,
}: {
  instrumentsData: GetInstrumentsResponseItem[] | undefined;
  isFetched: boolean;
  planEntryFeesBase: false | B2bCompanyPlan['fees']['entry_fee'];
}) =>
  useMemo(() => {
    if (!isFetched || planEntryFeesBase === false) {
      return [];
    }

    const instrumentsById = new Map(
      (instrumentsData || []).map((instrumentItem) => [instrumentItem.id, instrumentItem]),
    );

    return planEntryFeesBase
      .flatMap((entryFee) => {
        const instrumentItem = instrumentsById.get(entryFee.instrument_id);
        // CCB entry fees are always 0, no need to display them
        if (!instrumentItem || instrumentItem.type === B2bInstrumentType.CCB) {
          return [];
        }

        return [
          {
            ...entryFee,
            id: `${entryFee.instrument_id}:${entryFee.cash_source ?? ''}`,
            instrument: instrumentItem.short_name ?? '',
            isin: instrumentItem.isin ?? '',
            legal_status: instrumentItem.legal_status ?? '',
          },
        ];
      })
      .sort((left, right) => {
        const instrumentCompare = left.instrument.localeCompare(right.instrument);
        if (instrumentCompare !== 0) {
          return instrumentCompare;
        }
        return (left.cash_source ?? '').localeCompare(right.cash_source ?? '');
      });
  }, [instrumentsData, isFetched, planEntryFeesBase]);
